/**
 * Aprobación de Ausencias (ASIS-5363) — service
 */

import { apiClient, API_CONFIG } from '../../core';
import { buildQueryString } from '../../shared/queryBuilder';
import type { ApiResponse } from '../../shared';
import {
  mapAbsenceApprovalCounters,
  mapAbsenceApprovalCuts,
  mapAbsenceApprovalPage,
} from './absenceApproval.mapper';
import type {
  AbsenceApprovalCounters,
  AbsenceApprovalCuts,
  AbsenceApprovalPage,
  LC_AbsenceApprovalCountersOut,
  LC_GetAbsenceApprovalCountersIn,
  LC_GetAbsenceApprovalMonthlyCutsListOut,
  LC_ListAbsenceApprovalIn,
  LC_ListAbsenceApprovalListOut,
} from './absenceApproval.types';


// El backend ejecuta dbo.ca_ListarAtrasos una vez por cada empresa del universo resuelto
// (dimensional filters); para un corte mensual completo en un holding con varias empresas la
// suma de esas ejecuciones puede superar ampliamente el timeout por defecto del cliente
// (API_CONFIG.TIMEOUT, 10s). Tope acordado con el usuario: 10 minutos, alineado con
// AbsenceApprovalConstants.QUERY_TIMEOUT_SECONDS del backend (600s).
const ABSENCE_APPROVAL_TIMEOUT_MS = 600000;

export class AbsenceApprovalService {
  /**
   * Resolves the monthly cuts that feed the Año / Mes / Descripción filters.
   * Omitting `filterCompanyId` means the "Todos" option: only holding-wide cuts.
   */
  async getMonthlyCuts(
    filterCompanyId?: number | null,
    signal?: AbortSignal,
  ): Promise<ApiResponse<AbsenceApprovalCuts>> {
    const query = buildQueryString(filterCompanyId ? { filterCompanyId } : {});
    const endpoint = `${API_CONFIG.ENDPOINTS.ABSENCE_APPROVAL_MONTHLY_CUTS}${query}`;

    const response = await apiClient.get<LC_GetAbsenceApprovalMonthlyCutsListOut>(
      endpoint,
      { signal },
    );

    if (!response.success || !response.data) {
      return { ...response, data: null };
    }

    return { ...response, data: mapAbsenceApprovalCuts(response.data) };
  }

  /**
   * Loads a page of the grid. POST because the dimensional filters travel as up to seven CSV lists.
   */
  async list(
    params: LC_ListAbsenceApprovalIn,
    signal?: AbortSignal,
  ): Promise<ApiResponse<AbsenceApprovalPage>> {
    const response = await apiClient.post<LC_ListAbsenceApprovalListOut>(
      API_CONFIG.ENDPOINTS.ABSENCE_APPROVAL_SEARCH,
      params,
      { signal, timeoutMs: ABSENCE_APPROVAL_TIMEOUT_MS },
    );

    if (!response.success || !response.data) {
      return { ...response, data: null };
    }

    return { ...response, data: mapAbsenceApprovalPage(response.data) };
  }

  /**
   * Loads the four top counters for the cut with the current filters. Kept as a separate call from
   * `list` so the header can refresh without re-paginating the grid.
   */
  async getCounters(
    params: LC_GetAbsenceApprovalCountersIn,
    signal?: AbortSignal,
  ): Promise<ApiResponse<AbsenceApprovalCounters>> {
    const response = await apiClient.post<LC_AbsenceApprovalCountersOut>(
      API_CONFIG.ENDPOINTS.ABSENCE_APPROVAL_COUNTERS,
      params,
      { signal, timeoutMs: ABSENCE_APPROVAL_TIMEOUT_MS },
    );

    if (!response.success || !response.data) {
      return { ...response, data: null };
    }

    return { ...response, data: mapAbsenceApprovalCounters(response.data) };
  }
}

export const absenceApprovalService = new AbsenceApprovalService();
